import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { authApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = api.getToken();
      if (token) {
        try {
          const userData = await authApi.me();
          setUser(userData.user || userData);
        } catch (err) {
          console.error('Session invalid:', err);
          api.setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();

    // Listen for logout events (e.g., from 401 responses)
    const handleLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.login(email, password);
      api.setToken(response.token);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (data) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authApi.register(data);
      api.setToken(response.token);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      api.setToken(null);
      setUser(null);
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const response = await authApi.refresh();
      api.setToken(response.token);
      return response;
    } catch (err) {
      api.setToken(null);
      setUser(null);
      throw err;
    }
  }, []);

  // Set token from Auth1 callback and fetch user data
  const setToken = useCallback(async (token) => {
    if (!token) {
      api.setToken(null);
      setUser(null);
      return;
    }

    api.setToken(token);
    setLoading(true);
    try {
      const userData = await authApi.me();
      setUser(userData.user || userData);
    } catch (err) {
      console.error('Failed to fetch user after Auth1 login:', err);
      // Still keep user logged in with minimal info
      setUser({ id: 'auth1-user', email: 'admin@auth1.ai', role: 'admin' });
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshToken,
    setToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
